import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { font, Button, Input, Panel, EmptyState } from "@realtyflows/ui";
import { useTheme } from "../contexts/ThemeContext";
import { getContractFolders, getFolderStates, createContractFolder } from "../api";
import FolderStatusBadge from "../components/FolderStatusBadge";
import HealthScore from "../components/HealthScore";
import useResponsive from "../hooks/useResponsive";

/**
 * Folders — V1 Section B
 *
 * Top-level browsable list of every transaction folder owned by the user
 * (or visible in their team workspace). Folders are the central organizing
 * concept in RealtyFlows: every deal is a folder, every contract a doc
 * inside a folder.
 *
 * Layout:
 *   - Search box
 *   - Status filter chips (Draft / Active / In Escrow / Closed / Archived)
 *   - Quick-create folder
 *   - Folder cards (name, property, status, doc/upload counts, last activity)
 *
 * Filtered server-side via /contract-folders?status=...&search=...
 */
export default function Folders({ workspace }) {
  const t = useTheme();  // V2 ADR-003 Phase 4.3 wave 8
  const navigate = useNavigate();
  const responsive = useResponsive();

  const [folders, setFolders] = useState([]);
  const [states, setStates] = useState([]);
  const [statusFilter, setStatusFilter] = useState(""); // "" = all (excl archived)
  const [search, setSearch] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [loading, setLoading] = useState(true);

  // Quick-create
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [createError, setCreateError] = useState("");

  const fetchFolders = useCallback(() => {
    const params = {};
    if (workspace) params.workspace = workspace;
    if (statusFilter) params.status = statusFilter;
    if (search) params.search = search;
    if (showArchived) params.include_archived = "true";
    setLoading(true);
    getContractFolders(params)
      .then(setFolders)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [workspace, statusFilter, search, showArchived]);

  useEffect(() => { fetchFolders(); }, [fetchFolders]);
  useEffect(() => { getFolderStates().then(setStates).catch(() => setStates([])); }, []);

  const handleCreate = async () => {
    if (!newName.trim()) {
      setCreateError("Folder name is required");
      return;
    }
    setCreating(true);
    setCreateError("");
    try {
      const folder = await createContractFolder({ name: newName.trim() }, workspace);
      setNewName("");
      navigate(`/contracts/folders/${folder.id}`);
    } catch (err) {
      setCreateError(err.message || "Failed to create folder");
    } finally {
      setCreating(false);
    }
  };

  // Status filter chips: include "All" + all states except archived (gated separately)
  const filterChips = [
    { id: "", label: "All" },
    ...states.filter((s) => s.id !== "archived"),
  ];

  // Last-activity formatter — relative for recency
  const fmtRelative = (iso) => {
    if (!iso) return "Never";
    const ms = Date.now() - new Date(iso).getTime();
    const days = Math.floor(ms / (24 * 60 * 60 * 1000));
    if (days === 0) return "Today";
    if (days === 1) return "Yesterday";
    if (days < 30) return `${days}d ago`;
    if (days < 365) return `${Math.floor(days / 30)}mo ago`;
    return `${Math.floor(days / 365)}y ago`;
  };

  return (
    <div style={{ animation: "fadeSlide 0.3s ease" }}>
      {/* Quick create */}
      <Panel padding={16} style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <Input
              placeholder='Quick-create folder (e.g. "456 Oak St — Buyer")'
              value={newName}
              onChange={(e) => { setNewName(e.target.value); if (createError) setCreateError(""); }}
              onKeyDown={(e) => { if (e.key === "Enter") handleCreate(); }}
            />
          </div>
          <Button variant="primary" onClick={handleCreate} disabled={creating}>
            {creating ? "Creating..." : "Create folder"}
          </Button>
        </div>
        {createError && (
          <div style={{ marginTop: 8, fontSize: 11, color: t.coral, fontFamily: font.body }}>
            {createError}
          </div>
        )}
      </Panel>

      {/* Search + status chips */}
      <Panel padding={14} style={{ marginBottom: 16 }}>
        <Input
          placeholder="Search folders by name or property address..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10, alignItems: "center" }}>
          {filterChips.map((chip) => {
            const active = statusFilter === chip.id;
            return (
              <button
                key={chip.id || "all"}
                onClick={() => setStatusFilter(chip.id)}
                style={{
                  padding: "5px 12px",
                  borderRadius: 99,
                  background: active ? t.textPrimary : t.surfaceWarm,
                  color: active ? t.surface : t.textSecondary,
                  border: `1px solid ${active ? t.textPrimary : t.border}`,
                  fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: font.body,
                  transition: "all 0.15s",
                }}
              >
                {chip.label}
              </button>
            );
          })}
          <label style={{ display: "flex", alignItems: "center", gap: 6, marginLeft: "auto", fontSize: 11, color: t.textMuted, fontFamily: font.body, cursor: "pointer" }}>
            <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
            Show archived
          </label>
        </div>
      </Panel>

      {/* Folders list */}
      {loading ? (
        <div style={{ color: t.textMuted, padding: 40, textAlign: "center", fontFamily: font.body }}>
          Loading folders...
        </div>
      ) : folders.length === 0 ? (
        <Panel>
          <EmptyState
            title="No folders yet"
            sub="Create a folder above to organize a transaction's contracts and uploads, or create a deal — folders are auto-created for every deal."
          />
        </Panel>
      ) : (
        <div style={{
          display: "grid",
          gridTemplateColumns: responsive.isCompact ? "1fr" : "repeat(auto-fill, minmax(320px, 1fr))",
          gap: 14,
        }}>
          {folders.map((f) => (
            <Panel
              key={f.id}
              padding={16}
              onClick={() => navigate(`/contracts/folders/${f.id}`)}
              style={{ cursor: "pointer", transition: "transform 0.15s, border-color 0.15s" }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.borderColor = t.emerald; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.borderColor = ""; }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginBottom: 8 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, fontFamily: font.display, lineHeight: 1.3, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {f.name}
                </div>
                <FolderStatusBadge status={f.status} size="sm" />
              </div>
              {(f.property_address || f.deal_property_address) && (
                <div style={{ fontSize: 11, color: t.textMuted, marginBottom: 10, fontFamily: font.body, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {f.property_address || f.deal_property_address}
                </div>
              )}
              {f.client_name && (
                <div style={{ fontSize: 11, color: t.textSecondary, marginBottom: 10, fontFamily: font.body }}>
                  Client: {f.client_name}
                </div>
              )}

              <div style={{ display: "flex", gap: 12, fontSize: 11, color: t.textMuted, fontFamily: font.mono, paddingTop: 10, borderTop: `1px solid ${t.border}`, alignItems: "center" }}>
                <span><strong style={{ color: t.textPrimary }}>{f.form_count || 0}</strong> forms</span>
                <span><strong style={{ color: t.textPrimary }}>{f.upload_count || 0}</strong> uploads</span>
                <span style={{ marginLeft: "auto" }}>{fmtRelative(f.last_activity_at || f.updated_at)}</span>
              </div>
              {/* V2 Feature 2.3: inline health badge — lazy-fetched per card */}
              <div style={{ marginTop: 8 }}>
                <HealthScore folderId={f.id} compact />
              </div>
            </Panel>
          ))}
        </div>
      )}
    </div>
  );
}
